import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  CreateMemberRequestType,
  DeleteMemberRequestType,
  GetMembersRequestType,
  GetMembersResponseSchemaType,
  MemberResponseSchemaType,
  MemberSchemaType,
  UpdateMemberRequestType,
} from 'src/global/schemas/member.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

const buildMemberHeaders = (address?: string, network?: string) => ({
  ...(address && { 'x-offchain-address': address }),
  ...(network && { 'x-offchain-network': network }),
});

export const memberApi = createApi({
  reducerPath: REDUX_SLICE.MEMBER_API,
  baseQuery: axiosBaseQuery,
  tagTypes: ['Members', 'Member'],
  endpoints: builder => ({
    getMembers: builder.query<MemberSchemaType[], GetMembersRequestType & { address?: string; network?: string }>({
      query: ({ programId, page = 1, size = 10, address, network }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('size', size.toString());

        return {
          method: 'GET',
          path: `/v1/programs/${programId}/members?${queryParams.toString()}`,
          headers: buildMemberHeaders(address, network),
        };
      },
      transformResponse: (returnValue: GetMembersResponseSchemaType) => returnValue.data,
      providesTags: (result, error, { programId }) => [
        { type: 'Members', id: programId },
        ...(result || []).map(member => ({ type: 'Member' as const, id: member.addr })),
      ],
    }),

    createMember: builder.mutation<
      MemberSchemaType,
      CreateMemberRequestType & { programId: string; address?: string; network?: string }
    >({
      query: ({ programId, address, network, ...memberData }) => ({
        method: 'POST',
        path: `/v1/programs/${programId}/members`,
        data: memberData,
        headers: buildMemberHeaders(address, network),
        shouldSign: true,
      }),
      transformResponse: (returnValue: MemberResponseSchemaType) => returnValue.data,
      invalidatesTags: (result, error, { programId }) => [{ type: 'Members', id: programId }],
    }),

    updateMember: builder.mutation<MemberSchemaType, UpdateMemberRequestType & { address?: string; network?: string }>({
      query: ({ programId, memberAddr, address, network, ...memberData }) => ({
        method: 'PUT',
        path: `/v1/programs/${programId}/members/${memberAddr}`,
        data: memberData,
        headers: buildMemberHeaders(address, network),
        shouldSign: true,
      }),
      transformResponse: (returnValue: MemberResponseSchemaType) => returnValue.data,
      invalidatesTags: (result, error, { programId, memberAddr }) => [
        { type: 'Members', id: programId },
        { type: 'Member', id: memberAddr },
      ],
    }),

    deleteMember: builder.mutation<MemberSchemaType, DeleteMemberRequestType & { address?: string; network?: string }>({
      query: ({ programId, memberAddr, address, network }) => ({
        method: 'DELETE',
        path: `/v1/programs/${programId}/members/${memberAddr}`,
        headers: buildMemberHeaders(address, network),
        shouldSign: true,
      }),
      transformResponse: (returnValue: MemberResponseSchemaType) => returnValue.data,
      invalidatesTags: (result, error, { programId, memberAddr }) => [
        { type: 'Members', id: programId },
        { type: 'Member', id: memberAddr },
      ],
    }),
  }),
});

export const { useGetMembersQuery, useCreateMemberMutation, useUpdateMemberMutation, useDeleteMemberMutation } =
  memberApi;
